import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { UPDATE_USER_AUTH } from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { STORAGE_KEY } from '@/lib/i18n';

const SUPPORTED_LANGUAGES = [
  { key: 'en', name: 'English' },
  { key: 'de', name: 'Deutsch' },
  { key: 'es', name: 'Español' },
  { key: 'fr', name: 'Français' },
  { key: 'it', name: 'Italiano' },
  { key: 'nl', name: 'Nederlands' },
  { key: 'pt', name: 'Português' },
  { key: 'sv', name: 'Svenska' },
  { key: 'ja', name: '日本語' },
] as const;

/**
 * Syncs i18next language with the user's profile.language from ME_QUERY.
 * On auth load, sets i18next to the user's saved language.
 * Exposes setLanguage() for UI language switching.
 */
export function useLanguage() {
  const { i18n } = useTranslation();
  const me = useAuthStore((s) => s.me);
  const company = useAuthStore((s) => s.company);
  const [updateUserAuth] = useMutation(UPDATE_USER_AUTH);

  // Sync i18next language on auth load / backend change.
  // Fallback chain (matches scio1 personalProfile.js):
  //   user.profile.language → company.featureData.language → fallbackLng 'en'
  // Only fires when me.language or company language changes (NOT on i18n change).
  useEffect(() => {
    const userLang = me?.language;
    const companyLang = company?.featureData?.language;

    if (userLang) {
      if (userLang !== i18n.language) {
        i18n.changeLanguage(userLang);
        localStorage.setItem(STORAGE_KEY, userLang);
      }
    } else if (companyLang && companyLang !== i18n.language) {
      i18n.changeLanguage(companyLang);
      localStorage.setItem(STORAGE_KEY, companyLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.language, company?.featureData?.language]);

  const setLanguage = useCallback(
    async (lang: string) => {
      await i18n.loadLanguages(lang);
      await i18n.changeLanguage(lang);
      localStorage.setItem(STORAGE_KEY, lang);
      updateUserAuth({
        variables: { updateData: { language: lang } },
        refetchQueries: ['Me'],
      }).catch(() => {
        // Best-effort: local change is immediate, backend persistence optional
      });
    },
    [i18n, updateUserAuth],
  );

  return {
    language: i18n.language,
    setLanguage,
    languages: SUPPORTED_LANGUAGES,
  };
}
